import { createApi } from "@reduxjs/toolkit/query/react";

import {
  baseQuery,
  CREATE_CUSTOMS_BATCH,
  PROCESS_CUSTOMS_BATCH,
  GET_CUSTOMS_BATCH,
  GET_CUSTOMS_BATCHES,
  GET_CUSTOMS_DOCUMENTS,
  GET_CUSTOMS_DOCUMENT,
  GET_CUSTOMS_CLUSTERS,
  SEARCH_CUSTOMS_DOCUMENTS,
  DELETE_CUSTOMS_DOCUMENT,
  DELETE_CUSTOMS_CLUSTER,
  GET_CLUSTER_AUDIT,
  GET_CLUSTER_AUDITS,
  RUN_CLUSTER_AUDIT,
  QUEUE_CLUSTER_AUDIT,
  QUEUE_MULTIPLE_AUDITS,
  GET_AUDIT_QUEUE_STATS,
  GET_VALUE_DISCREPANCIES,
  GET_DUTY_DISCREPANCIES,
  GET_CLASSIFICATION_DISCREPANCIES,
  UPDATE_DISCREPANCY_REVIEW_STATUS,
  ADD_DISCREPANCY_COMMENT,
  REMOVE_DISCREPANCY_COMMENT,
  RE_EXTRACT_DISCREPANCY_DATA,
  UPDATE_DISCREPANCY_VALUES,
  UPDATE_CLASSIFICATION_DISCREPANCY_VALUES,
  DELETE_DISCREPANCY,
  GET_ENTRY_LINKING_STATS,
  ADD_DOCUMENT_IDENTIFIER,
  UPDATE_DOCUMENT_IDENTIFIER,
  REMOVE_DOCUMENT_IDENTIFIER,
} from "@/graphql";

export const customsDocumentApi = createApi({
  reducerPath: "api/customsDocument",
  tagTypes: ["CustomsBatches", "CustomsDocuments", "CustomsClusters", "ClusterAudits"],
  baseQuery,
  endpoints: (builder) => ({
    // Create a new batch with pre-signed upload URLs
    createCustomsBatch: builder.mutation<
      CreateCustomsBatchResponse["createCustomsBatch"],
      CreateCustomsBatchInput
    >({
      query: (input) => ({
        document: CREATE_CUSTOMS_BATCH,
        variables: { input },
      }),
      transformResponse: (response: CreateCustomsBatchResponse) => {
        return response.createCustomsBatch;
      },
      invalidatesTags: ["CustomsBatches"],
    }),

    // Manually trigger processing for all UPLOADED documents in a batch
    processCustomsBatch: builder.mutation<
      ProcessCustomsBatchResponse["processCustomsBatch"],
      { batchId: string; tenantId?: string }
    >({
      query: ({ batchId, tenantId }) => ({
        document: PROCESS_CUSTOMS_BATCH,
        variables: { batchId, tenantId },
      }),
      transformResponse: (response: ProcessCustomsBatchResponse) => {
        return response.processCustomsBatch;
      },
      invalidatesTags: ["CustomsBatches", "CustomsDocuments"],
    }),

    // Get batch status with all documents
    getCustomsBatch: builder.query<
      CustomsBatchResponse["customsBatch"],
      string
    >({
      query: (batchId) => ({
        document: GET_CUSTOMS_BATCH,
        variables: { batchId },
      }),
      transformResponse: (response: CustomsBatchResponse) => {
        return response.customsBatch;
      },
      providesTags: ["CustomsBatches"],
    }),

    // List batches for tenant
    getCustomsBatches: builder.query<
      CustomsBatchesResponse["customsBatches"],
      {
        tenantId?: string;
        clientId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_BATCHES,
        variables,
      }),
      transformResponse: (response: CustomsBatchesResponse) => {
        return response.customsBatches;
      },
      providesTags: ["CustomsBatches"],
    }),

    // List documents with filters
    getCustomsDocuments: builder.query<
      CustomsDocumentsResponse["customsDocuments"],
      {
        tenantId?: string;
        clientId?: string;
        batchId?: string;
        status?: CustomsDocumentStatus;
        clusterId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_DOCUMENTS,
        variables,
      }),
      transformResponse: (response: CustomsDocumentsResponse) => {
        return response.customsDocuments;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // Get single document with download URL
    getCustomsDocument: builder.query<
      CustomsDocumentResponse["customsDocument"],
      { docId: string; tenantId?: string }
    >({
      query: ({ docId, tenantId }) => ({
        document: GET_CUSTOMS_DOCUMENT,
        variables: { docId, tenantId },
      }),
      transformResponse: (response: CustomsDocumentResponse) => {
        return response.customsDocument;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // List clusters with pagination and optional filters
    // Use 'identifier' to search by any identifier type (BL, Container, PO, Invoice, Entry)
    getCustomsClusters: builder.query<
      CustomsClustersResponse["customsClusters"],
      {
        tenantId?: string;
        clientId?: string;
        entryNumber?: string;
        identifier?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CUSTOMS_CLUSTERS,
        variables,
      }),
      transformResponse: (response: CustomsClustersResponse) => {
        return response.customsClusters;
      },
      providesTags: ["CustomsClusters"],
    }),

    // Search documents by identifier or text
    searchCustomsDocuments: builder.query<
      CustomsDocumentsResponse["customsDocuments"],
      {
        query: string;
        tenantId?: string;
        clientId?: string;
        documentType?: CustomsDocumentType;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: SEARCH_CUSTOMS_DOCUMENTS,
        variables,
      }),
      transformResponse: (response: CustomsDocumentsResponse) => {
        return response.customsDocuments;
      },
      providesTags: ["CustomsDocuments"],
    }),

    // Delete a customs document
    deleteCustomsDocument: builder.mutation<
      boolean,
      { docId: string; tenantId?: string }
    >({
      query: ({ docId, tenantId }) => ({
        document: DELETE_CUSTOMS_DOCUMENT,
        variables: { docId, tenantId },
      }),
      transformResponse: (response: { deleteCustomsDocument: boolean }) => {
        return response.deleteCustomsDocument;
      },
      invalidatesTags: ["CustomsDocuments", "CustomsBatches", "CustomsClusters"],
    }),

    // Delete a customs cluster and all its documents
    deleteCustomsCluster: builder.mutation<
      { success: boolean; deletedDocuments: number },
      { clusterId: string; tenantId?: string }
    >({
      query: ({ clusterId, tenantId }) => ({
        document: DELETE_CUSTOMS_CLUSTER,
        variables: { clusterId, tenantId },
      }),
      transformResponse: (response: {
        deleteCustomsCluster: { success: boolean; deletedDocuments: number };
      }) => {
        return response.deleteCustomsCluster;
      },
      invalidatesTags: ["CustomsClusters", "CustomsDocuments", "CustomsBatches", "ClusterAudits"],
    }),

    // Get the most recent audit for a cluster
    getClusterAudit: builder.query<
      ClusterAuditResponse["clusterAudit"],
      { clusterId: string; tenantId?: string }
    >({
      query: ({ clusterId, tenantId }) => ({
        document: GET_CLUSTER_AUDIT,
        variables: { clusterId, tenantId },
      }),
      transformResponse: (response: ClusterAuditResponse) => {
        return response.clusterAudit;
      },
      providesTags: ["ClusterAudits"],
    }),

    // List audits with filters
    getClusterAudits: builder.query<
      ClusterAuditsResponse["clusterAudits"],
      {
        tenantId?: string;
        clientId?: string;
        status?: ClusterAuditStatus;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CLUSTER_AUDITS,
        variables,
      }),
      transformResponse: (response: ClusterAuditsResponse) => {
        return response.clusterAudits;
      },
      providesTags: ["ClusterAudits"],
    }),

    // Run AI audit on a cluster
    runClusterAudit: builder.mutation<
      RunClusterAuditResponse["runClusterAudit"],
      { clusterId: string; tenantId?: string }
    >({
      query: ({ clusterId, tenantId }) => ({
        document: RUN_CLUSTER_AUDIT,
        variables: { clusterId, tenantId },
      }),
      transformResponse: (response: RunClusterAuditResponse) => {
        return response.runClusterAudit;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Queue a cluster audit for background processing
    queueClusterAudit: builder.mutation<
      QueueClusterAuditResponse["queueClusterAudit"],
      { clusterId: string; tenantId?: string; priority?: number }
    >({
      query: ({ clusterId, tenantId, priority }) => ({
        document: QUEUE_CLUSTER_AUDIT,
        variables: { clusterId, tenantId, priority },
      }),
      transformResponse: (response: QueueClusterAuditResponse) => {
        return response.queueClusterAudit;
      },
      invalidatesTags: ["CustomsClusters"],
    }),

    // Queue multiple cluster audits (e.g., "Audit All")
    queueMultipleAudits: builder.mutation<
      QueueMultipleAuditsResponse["queueMultipleAudits"],
      { clusterIds: string[]; tenantId?: string }
    >({
      query: ({ clusterIds, tenantId }) => ({
        document: QUEUE_MULTIPLE_AUDITS,
        variables: { clusterIds, tenantId },
      }),
      transformResponse: (response: QueueMultipleAuditsResponse) => {
        return response.queueMultipleAudits;
      },
      invalidatesTags: ["CustomsClusters"],
    }),

    // Get audit queue statistics
    getAuditQueueStats: builder.query<
      AuditQueueStatsResponse["auditQueueStats"],
      { tenantId?: string }
    >({
      query: ({ tenantId }) => ({
        document: GET_AUDIT_QUEUE_STATS,
        variables: { tenantId },
      }),
      transformResponse: (response: AuditQueueStatsResponse) => {
        return response.auditQueueStats;
      },
    }),

    // Get aggregated value discrepancies
    getValueDiscrepancies: builder.query<
      ValueDiscrepanciesResponse["valueDiscrepancies"],
      {
        tenantId?: string;
        clientId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_VALUE_DISCREPANCIES,
        variables,
      }),
      transformResponse: (response: ValueDiscrepanciesResponse) => {
        return response.valueDiscrepancies;
      },
      providesTags: ["ClusterAudits"],
    }),

    // Get aggregated duty calculation discrepancies
    getDutyDiscrepancies: builder.query<
      DutyDiscrepanciesResponse["dutyDiscrepancies"],
      {
        tenantId?: string;
        clientId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_DUTY_DISCREPANCIES,
        variables,
      }),
      transformResponse: (response: DutyDiscrepanciesResponse) => {
        return response.dutyDiscrepancies;
      },
      providesTags: ["ClusterAudits"],
    }),

    // Get aggregated HTS classification discrepancies
    getClassificationDiscrepancies: builder.query<
      {
        data: ClassificationDiscrepancyItem[];
        totals: ClassificationDiscrepancyTotals;
        countDocs: number;
        pageNo: number;
        pageSize: number;
      },
      {
        tenantId?: string;
        clientId?: string;
        pageNo?: number;
        pageSize?: number;
      }
    >({
      query: (variables) => ({
        document: GET_CLASSIFICATION_DISCREPANCIES,
        variables,
      }),
      transformResponse: (response: ClassificationDiscrepanciesResponse) => {
        return response.classificationDiscrepancies;
      },
      providesTags: ["ClusterAudits"],
    }),

    // Update review status on a discrepancy
    updateDiscrepancyReviewStatus: builder.mutation<
      DiscrepancyReviewResponse,
      {
        auditId: string;
        discrepancyId: string;
        status: "confirmed" | "not_an_issue";
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, status, tenantId }) => ({
        document: UPDATE_DISCREPANCY_REVIEW_STATUS,
        variables: { auditId, discrepancyId, status, tenantId },
      }),
      transformResponse: (response: { updateDiscrepancyReviewStatus: DiscrepancyReviewResponse }) => {
        return response.updateDiscrepancyReviewStatus;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Add a comment to a discrepancy
    addDiscrepancyComment: builder.mutation<
      DiscrepancyReviewResponse,
      {
        auditId: string;
        discrepancyId: string;
        text: string;
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, text, tenantId }) => ({
        document: ADD_DISCREPANCY_COMMENT,
        variables: { auditId, discrepancyId, text, tenantId },
      }),
      transformResponse: (response: { addDiscrepancyComment: DiscrepancyReviewResponse }) => {
        return response.addDiscrepancyComment;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Remove a comment from a discrepancy
    removeDiscrepancyComment: builder.mutation<
      DiscrepancyReviewResponse,
      {
        auditId: string;
        discrepancyId: string;
        commentId: string;
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, commentId, tenantId }) => ({
        document: REMOVE_DISCREPANCY_COMMENT,
        variables: { auditId, discrepancyId, commentId, tenantId },
      }),
      transformResponse: (response: { removeDiscrepancyComment: DiscrepancyReviewResponse }) => {
        return response.removeDiscrepancyComment;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Re-extract structured data from a discrepancy's description
    reExtractDiscrepancyData: builder.mutation<
      {
        success: boolean;
        declaredValue?: number;
        actualValue?: number;
        discrepancyAmount?: number;
        discrepancyType?: string;
        error?: string;
      },
      {
        auditId: string;
        discrepancyId: string;
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, tenantId }) => ({
        document: RE_EXTRACT_DISCREPANCY_DATA,
        variables: { auditId, discrepancyId, tenantId },
      }),
      transformResponse: (response: { reExtractDiscrepancyData: { success: boolean; declaredValue?: number; actualValue?: number; discrepancyAmount?: number; discrepancyType?: string; error?: string } }) => {
        return response.reExtractDiscrepancyData;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Manually update discrepancy values
    updateDiscrepancyValues: builder.mutation<
      { success: boolean; error?: string },
      {
        auditId: string;
        discrepancyId: string;
        declaredValue: number;
        actualValue: number;
        lineNumbers?: number[];
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, declaredValue, actualValue, lineNumbers, tenantId }) => ({
        document: UPDATE_DISCREPANCY_VALUES,
        variables: { auditId, discrepancyId, declaredValue, actualValue, lineNumbers, tenantId },
      }),
      transformResponse: (response: { updateDiscrepancyValues: { success: boolean; error?: string } }) => {
        return response.updateDiscrepancyValues;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Manually update classification discrepancy values
    updateClassificationDiscrepancyValues: builder.mutation<
      { success: boolean; error?: string },
      {
        auditId: string;
        discrepancyId: string;
        filedHtsCode: string;
        correctHtsCode: string;
        filedDutyRate: number;
        correctDutyRate: number;
        lineNumber?: number;
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, filedHtsCode, correctHtsCode, filedDutyRate, correctDutyRate, lineNumber, tenantId }) => ({
        document: UPDATE_CLASSIFICATION_DISCREPANCY_VALUES,
        variables: { auditId, discrepancyId, filedHtsCode, correctHtsCode, filedDutyRate, correctDutyRate, lineNumber, tenantId },
      }),
      transformResponse: (response: { updateClassificationDiscrepancyValues: { success: boolean; error?: string } }) => {
        return response.updateClassificationDiscrepancyValues;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Get entry-document linking stats (accurate across all data)
    getEntryLinkingStats: builder.query<
      {
        totalEntries: number;
        linkedEntries: number;
        auditResults: {
          approved: number;
          needsReview: number;
          discrepancies: number;
          notAudited: number;
        };
        missingProducts: number;
        totalValue: number;
        totalDuty: number;
        timeSeries: { month: string; importValue: number; duties: number }[];
      },
      { tenantId?: string; clientId?: string }
    >({
      query: (variables) => ({
        document: GET_ENTRY_LINKING_STATS,
        variables,
      }),
      transformResponse: (response: {
        entryLinkingStats: {
          totalEntries: number;
          linkedEntries: number;
          auditResults: {
            approved: number;
            needsReview: number;
            discrepancies: number;
            notAudited: number;
          };
          missingProducts: number;
          totalValue: number;
          totalDuty: number;
          timeSeries: { month: string; importValue: number; duties: number }[];
        };
      }) => {
        return response.entryLinkingStats;
      },
      providesTags: ["CustomsClusters"],
    }),

    // Delete a discrepancy from an audit
    deleteDiscrepancy: builder.mutation<
      { success: boolean; error?: string },
      {
        auditId: string;
        discrepancyId: string;
        tenantId?: string;
      }
    >({
      query: ({ auditId, discrepancyId, tenantId }) => ({
        document: DELETE_DISCREPANCY,
        variables: { auditId, discrepancyId, tenantId },
      }),
      transformResponse: (response: { deleteDiscrepancy: { success: boolean; error?: string } }) => {
        return response.deleteDiscrepancy;
      },
      invalidatesTags: ["ClusterAudits"],
    }),

    // Add an identifier to a document
    addDocumentIdentifier: builder.mutation<
      CustomsDocument,
      { docId: string; type: CustomsIdentifierType; value: string; tenantId?: string }
    >({
      query: ({ docId, type, value, tenantId }) => ({
        document: ADD_DOCUMENT_IDENTIFIER,
        variables: { docId, type, value, tenantId },
      }),
      transformResponse: (response: { addDocumentIdentifier: CustomsDocument }) => {
        return response.addDocumentIdentifier;
      },
      invalidatesTags: ["CustomsDocuments", "CustomsClusters"],
    }),

    // Update an identifier on a document
    updateDocumentIdentifier: builder.mutation<
      CustomsDocument,
      { docId: string; index: number; type: CustomsIdentifierType; value: string; tenantId?: string }
    >({
      query: ({ docId, index, type, value, tenantId }) => ({
        document: UPDATE_DOCUMENT_IDENTIFIER,
        variables: { docId, index, type, value, tenantId },
      }),
      transformResponse: (response: { updateDocumentIdentifier: CustomsDocument }) => {
        return response.updateDocumentIdentifier;
      },
      invalidatesTags: ["CustomsDocuments", "CustomsClusters"],
    }),

    // Remove an identifier from a document
    removeDocumentIdentifier: builder.mutation<
      CustomsDocument,
      { docId: string; index: number; tenantId?: string }
    >({
      query: ({ docId, index, tenantId }) => ({
        document: REMOVE_DOCUMENT_IDENTIFIER,
        variables: { docId, index, tenantId },
      }),
      transformResponse: (response: { removeDocumentIdentifier: CustomsDocument }) => {
        return response.removeDocumentIdentifier;
      },
      invalidatesTags: ["CustomsDocuments", "CustomsClusters"],
    }),
  }),
});

export const {
  useCreateCustomsBatchMutation,
  useProcessCustomsBatchMutation,
  useGetCustomsBatchQuery,
  useLazyGetCustomsBatchQuery,
  useGetCustomsBatchesQuery,
  useGetCustomsDocumentsQuery,
  useGetCustomsDocumentQuery,
  useLazyGetCustomsDocumentQuery,
  useGetCustomsClustersQuery,
  useLazyGetCustomsClustersQuery,
  useSearchCustomsDocumentsQuery,
  useLazySearchCustomsDocumentsQuery,
  useDeleteCustomsDocumentMutation,
  useDeleteCustomsClusterMutation,
  useGetClusterAuditQuery,
  useLazyGetClusterAuditQuery,
  useGetClusterAuditsQuery,
  useRunClusterAuditMutation,
  useQueueClusterAuditMutation,
  useQueueMultipleAuditsMutation,
  useGetAuditQueueStatsQuery,
  useGetValueDiscrepanciesQuery,
  useGetDutyDiscrepanciesQuery,
  useGetClassificationDiscrepanciesQuery,
  useUpdateDiscrepancyReviewStatusMutation,
  useAddDiscrepancyCommentMutation,
  useRemoveDiscrepancyCommentMutation,
  useReExtractDiscrepancyDataMutation,
  useUpdateDiscrepancyValuesMutation,
  useUpdateClassificationDiscrepancyValuesMutation,
  useDeleteDiscrepancyMutation,
  useGetEntryLinkingStatsQuery,
  useAddDocumentIdentifierMutation,
  useUpdateDocumentIdentifierMutation,
  useRemoveDocumentIdentifierMutation,
} = customsDocumentApi;
